// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
import { MenuItem, Switch, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import type { Lang } from "@/hooks/useLang/types";
import useLang from "@/hooks/useLang";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import { isRtlLang } from "@/utils";
import type { TyLangValues } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyPriceRow = {
    key: string;
    originalId?: string;
    amount: string;
    /** Keyed by language rather than a fixed pair — a price tier is named in whatever the store sells in. */
    label: TyLangValues;
    showLabel: boolean;
    hasDiscount: boolean;
    discountType: "percentage" | "fixed";
    discountAmount: string;
};

export type ProductSizePopupDetails = {
    initialPrice?: TyPriceRow;
    onSave: (price: TyPriceRow) => void;
    hideLabelInputs?: boolean;
};

type Props = {
    onClose: () => void;
    details?: ProductSizePopupDetails;
};

// --- Helpers ------------------------------------------------------------------------------------

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": { color: "var(--color-brand)" },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-full)",
        "& fieldset": { borderColor: "var(--color-gray-01)", borderWidth: "1.5px" },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": { borderColor: "var(--color-gray-02)" },
        "&.Mui-focused fieldset": { borderColor: "var(--color-brand)", borderWidth: "1.5px" },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

// --- Constants ----------------------------------------------------------------------------------

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Helpers ------------------------------------------------------------------------------------

function makeBlankPrice(): TyPriceRow {
    return {
        key: crypto.randomUUID(),
        amount: "",
        label: {},
        showLabel: true,
        hasDiscount: false,
        discountType: "percentage",
        discountAmount: "",
    };
}

// --- Logic --------------------------------------------------------------------------------------

export default function ProductSizePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages } = useStoreLanguages();
    const isEdit = Boolean(details?.initialPrice);
    const hideLabelInputs = details?.hideLabelInputs ?? false;

    const [price, setPrice] = useState<TyPriceRow>(() => details?.initialPrice ?? makeBlankPrice());

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");

    const setLabel = (target: Lang, value: string) => setPrice((prev) => ({ ...prev, label: { ...prev.label, [target]: value } }));

    const setField = <K extends keyof TyPriceRow>(key: K, value: TyPriceRow[K]) => setPrice((prev) => ({ ...prev, [key]: value }));

    const handleSave = () => {
        if (!price.amount.trim()) return;
        details?.onSave(price);
        onClose();
    };

    return (
        <div className="product-size-popup">
            <div className="psp-header">
                <p className="psp-title">{isEdit ? t.pricePopup.editTitle : t.pricePopup.createTitle}</p>
                <button type="button" className="psp-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <div className="psp-body">
                {/* Price amount */}
                <TextField
                    fullWidth
                    type="number"
                    label={t.formProduct.priceAmount}
                    value={price.amount}
                    onChange={(e) => setField("amount", e.target.value)}
                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                    sx={textFieldSx}
                />

                {/* Label section */}
                <div className="psp-section">
                    <div className="psp-section-header">
                        <span className="psp-section-title">{t.formProduct.showLabel}</span>
                        <Switch checked={price.showLabel} onChange={(e) => setField("showLabel", e.target.checked)} sx={switchSx} />
                    </div>
                    {price.showLabel && !hideLabelInputs && (
                        <div className="psp-row" dir="ltr">
                            {languages.map((entry) => (
                                <TextField
                                    key={entry}
                                    fullWidth
                                    label={t.languages[entry]}
                                    value={price.label[entry] ?? ""}
                                    onChange={(e) => setLabel(entry, e.target.value)}
                                    sx={getTextFieldSx(isRtlLang(entry) ? "rtl" : "ltr")}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Discount section */}
                <div className="psp-section">
                    <div className="psp-section-header">
                        <div className="psp-section-icon">
                            <img src="/icons/inputs-group-icon.svg" alt="" width={17} height={17} />
                        </div>
                        <span className="psp-section-title">{t.formProduct.discountSection}</span>
                        <Switch checked={price.hasDiscount} onChange={(e) => setField("hasDiscount", e.target.checked)} sx={switchSx} />
                    </div>
                    {price.hasDiscount && (
                        <div className="psp-row">
                            <TextField
                                select
                                fullWidth
                                label={t.formProduct.discountType}
                                value={price.discountType}
                                onChange={(e) => setField("discountType", e.target.value as "percentage" | "fixed")}
                                sx={textFieldSx}
                            >
                                <MenuItem value="percentage">{t.formProduct.discountPercentage}</MenuItem>
                                <MenuItem value="fixed">{t.formProduct.discountFixed}</MenuItem>
                            </TextField>
                            <TextField
                                fullWidth
                                type="number"
                                label={t.formProduct.discountAmount}
                                value={price.discountAmount}
                                onChange={(e) => setField("discountAmount", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                sx={textFieldSx}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="psp-footer">
                <button type="button" className="psp-btn psp-btn--cancel" onClick={onClose}>
                    {t.pricePopup.cancel}
                </button>
                <button type="button" className="psp-btn psp-btn--save" onClick={handleSave} disabled={!price.amount.trim()}>
                    {t.pricePopup.save}
                </button>
            </div>
        </div>
    );
}
